/** A segment departs when its `from` activity ends and arrives when its `to` activity starts. */

import { simplifyLatLng, type LatLng } from '../map/simplifyPath';
import { haversineDistanceMeters } from './tripRouteMatching';

export type { LatLng };

export type ActivityTimes = Map<string, Map<number, { startMs: number; endMs: number }>>;

export interface PlaybackSegmentInput {
  /** GeoJSON order: [lng, lat]. */
  coordinates: [number, number][];
  vehicleShiftId?: string;
  fromActivityNumber?: number;
  toActivityNumber?: number;
  onboardCount: number;
}

export interface TimedSegment {
  path: LatLng[];
  times: number[];
  onboardCount: number;
}

export interface VehicleTrack {
  vehicleShiftId: string;
  color: string;
  /** Sorted by departure time. */
  segments: TimedSegment[];
}

// ponytail: /paths has no duration, so depot legs and idle legs assume 30 km/h; use its travel time once it exists.
const ASSUMED_SPEED_MPS = 30 / 3.6;
/** Slower than this, the gap between activities includes idle time, not just driving. */
const MIN_DRIVING_SPEED_MPS = 10 / 3.6;
/** Douglas-Peucker tolerance, about 10 m. */
const SIMPLIFY_TOLERANCE_DEG = 0.0001;

const distanceMeters = (a: LatLng, b: LatLng) => haversineDistanceMeters(a[0], a[1], b[0], b[1]);

function vehicleColor(id: string): string {
  let hash = 0;
  for (let i = 0; i < id.length; i += 1) hash = (hash * 31 + id.charCodeAt(i)) | 0;
  return `hsl(${Math.abs(hash) % 360}, 75%, 42%)`;
}

export function buildVehicleTracks(
  segments: PlaybackSegmentInput[],
  activityTimes: ActivityTimes
): VehicleTrack[] {
  const byVehicle = new Map<string, TimedSegment[]>();
  segments.forEach((segment) => {
    const sid = segment.vehicleShiftId;
    if (!sid || segment.coordinates.length < 2) return;
    const path = simplifyLatLng(segment.coordinates.map(([lng, lat]) => [lat, lng] as LatLng), SIMPLIFY_TOLERANCE_DEG);
    const cumulative = [0];
    for (let i = 1; i < path.length; i += 1) {
      cumulative.push(cumulative[i - 1] + distanceMeters(path[i - 1], path[i]));
    }
    const total = cumulative[cumulative.length - 1];
    const inner = activityTimes.get(sid);
    const from = segment.fromActivityNumber != null ? inner?.get(segment.fromActivityNumber) : undefined;
    const to = segment.toActivityNumber != null ? inner?.get(segment.toActivityNumber) : undefined;
    const travelMs = (total / ASSUMED_SPEED_MPS) * 1000;
    const scheduledDepartMs = from?.endMs ?? (to ? to.startMs - travelMs : null);
    const arriveMs = to?.startMs ?? (from ? from.endMs + travelMs : null);
    if (scheduledDepartMs == null || arriveMs == null) return;
    // Idle leg: wait at the last stop, then leave in time to arrive on schedule.
    const isIdle = total / Math.max(1, (arriveMs - scheduledDepartMs) / 1000) < MIN_DRIVING_SPEED_MPS;
    const departMs = isIdle ? arriveMs - travelMs : scheduledDepartMs;
    const duration = Math.max(0, arriveMs - departMs);
    const times = cumulative.map((d) => departMs + (total > 0 ? (d / total) * duration : 0));
    const list = byVehicle.get(sid) ?? [];
    list.push({ path, times, onboardCount: segment.onboardCount });
    byVehicle.set(sid, list);
  });
  return Array.from(byVehicle, ([vehicleShiftId, list]) => ({
    vehicleShiftId,
    color: vehicleColor(vehicleShiftId),
    segments: list.sort((a, b) => a.times[0] - b.times[0]),
  }));
}

/** Last index i with times[i] <= t (or -1). */
function lastIndexAtOrBefore(times: number[], t: number): number {
  let lo = 0;
  let hi = times.length - 1;
  let found = -1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (times[mid] <= t) {
      found = mid;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }
  return found;
}

function pointAt(segment: TimedSegment, t: number): LatLng {
  const { path, times } = segment;
  const i = lastIndexAtOrBefore(times, t);
  if (i < 0) return path[0];
  if (i >= path.length - 1) return path[path.length - 1];
  const span = times[i + 1] - times[i];
  const f = span > 0 ? (t - times[i]) / span : 1;
  const a = path[i];
  const b = path[i + 1];
  return [a[0] + (b[0] - a[0]) * f, a[1] + (b[1] - a[1]) * f];
}

/**
 * Where the vehicle is at `t`: on the segment covering `t`, or waiting at the end of the last
 * finished segment, with that segment's onboard count. Null before the first departure or after
 * the last arrival.
 */
export function vehiclePositionAt(
  track: VehicleTrack,
  t: number
): { position: LatLng; onboardCount: number } | null {
  const { segments } = track;
  if (segments.length === 0) return null;
  const first = segments[0];
  const last = segments[segments.length - 1];
  if (t < first.times[0] || t > last.times[last.times.length - 1]) return null;
  let current: TimedSegment = first;
  for (const segment of segments) {
    if (segment.times[0] > t) break;
    current = segment;
  }
  return { position: pointAt(current, t), onboardCount: current.onboardCount };
}

/**
 * The part of `segment` travelled within [t - tailMs, t], each point paired with its time.
 * Empty when the segment does not overlap that window.
 */
const EMPTY_TRAIL: { points: LatLng[]; times: number[] } = { points: [], times: [] };

export function trailWithin(
  segment: TimedSegment,
  t: number,
  tailMs: number
): { points: LatLng[]; times: number[] } {
  const { path, times } = segment;
  const start = t - tailMs;
  const end = Math.min(t, times[times.length - 1]);
  if (end < times[0] || start > end) return EMPTY_TRAIL;
  const from = Math.max(start, times[0]);
  const points: LatLng[] = [pointAt(segment, from)];
  const pointTimes = [from];
  for (let i = lastIndexAtOrBefore(times, from) + 1; i < times.length && times[i] < end; i += 1) {
    points.push(path[i]);
    pointTimes.push(times[i]);
  }
  points.push(pointAt(segment, end));
  pointTimes.push(end);
  return { points, times: pointTimes };
}
